import {
  Party,
  PartyFormData,
  PartyFilters,
  PartyListResponse,
  LedgerTransactionRow,
  LedgerFilters,
  AgeingRow,
  AgeingFilters,
  Employee,
} from '../types/party.types'

class PartyService {
  private baseUrl = '/api/parties'

  async getParties(filters: PartyFilters): Promise<PartyListResponse> {
    // Mock implementation - replace with actual API call
    const mockParties: Party[] = [
      {
        id: '1',
        partyCode: 'CUST-001',
        name: 'ABC Trading Company',
        printAs: 'ABC Trading Co.',
        partyType: 'Customer' as any,
        type: 'Organization' as any,
        categoryId: 'cat1',
        categoryName: 'Wholesale',
        email: 'contact@abctrading.com',
        phone: '+252-61-1234567',
        mobile: '+252-61-1234567',
        vatRegNo: 'VAT123456',
        bin: 'BIN789012',
        address: {
          country: 'Somalia',
          region: 'Woqooyi Galbeed',
          district: 'Hargeisa',
          detailAddress: '123 Main Street, Hargeisa',
        },
        associatedLedger: {
          accountCode: '1300',
          accountName: 'Accounts Receivable - ABC Trading',
          balance: 150000,
        },
        status: 'Active' as any,
        createdBy: 'Admin',
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-01-15T10:00:00Z',
        isEditable: true,
      },
      {
        id: '2',
        partyCode: 'VEND-001',
        name: 'XYZ Suppliers Ltd',
        printAs: 'XYZ Suppliers',
        partyType: 'Vendor' as any,
        type: 'Organization' as any,
        categoryId: 'cat2',
        categoryName: 'Supplier',
        email: 'info@xyzsuppliers.com',
        mobile: '+252-61-7654321',
        address: {
          country: 'Somalia',
          region: 'Banaadir',
          district: 'Mogadishu',
          detailAddress: '456 Commerce Road, Mogadishu',
        },
        associatedLedger: {
          accountCode: '2100',
          accountName: 'Accounts Payable - XYZ Suppliers',
          balance: -85000,
        },
        status: 'Active' as any,
        createdBy: 'Admin',
        createdAt: '2024-01-16T11:00:00Z',
        updatedAt: '2024-01-16T11:00:00Z',
        isEditable: true,
      },
    ]

    return {
      data: mockParties,
      total: mockParties.length,
      page: filters.page || 1,
      limit: filters.limit || 20,
    }
  }

  async getPartyById(id: string): Promise<Party> {
    // Mock implementation
    const response = await this.getParties({})
    const party = response.data.find((p) => p.id === id)
    if (!party) throw new Error('Party not found')
    return party
  }

  async createParty(data: PartyFormData): Promise<Party> {
    // Mock implementation
    console.log('Creating party:', data)
    return {
      id: Date.now().toString(),
      partyCode: `${data.partyType.substring(0, 4).toUpperCase()}-${Date.now()}`,
      ...data,
      createdBy: 'Current User',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isEditable: true,
    } as Party
  }

  async updateParty(id: string, data: Partial<PartyFormData>): Promise<Party> {
    // Mock implementation
    console.log('Updating party:', id, data)
    const party = await this.getPartyById(id)
    return {
      ...party,
      ...data,
      updatedAt: new Date().toISOString(),
    } as Party
  }

  async togglePartyStatus(id: string): Promise<Party> {
    const party = await this.getPartyById(id)
    return this.updateParty(id, {
      status: party.status === 'Active' ? 'Inactive' : 'Active',
    } as any)
  }

  async getPartyLedger(
    partyId: string,
    filters: LedgerFilters,
  ): Promise<LedgerTransactionRow[]> {
    // Mock implementation
    return [
      {
        id: '1',
        partyName: 'ABC Trading Company',
        partyCode: 'CUST-001',
        category: 'Wholesale',
        date: '2024-01-15',
        voucherNo: 'RV-2024-001',
        refBillVoucherNo: 'INV-2024-001',
        description: 'Payment received for Invoice INV-2024-001',
        accountCode: '1300',
        accountName: 'Accounts Receivable',
        otherAccountCode: '1100',
        otherAccountName: 'Cash',
        previousBalance: 0,
        debit: 150000,
        credit: 0,
        balance: 150000,
      },
    ]
  }

  async getReceivableAgeing(filters: AgeingFilters): Promise<AgeingRow[]> {
    // Mock implementation
    return [
      {
        partyCode: 'CUST-001',
        partyName: 'ABC Trading Company',
        category: 'Wholesale',
        total: 150000,
        bucket_0_30: 50000,
        bucket_31_90: 40000,
        bucket_90_180: 30000,
        bucket_180_270: 20000,
        bucket_270_365: 10000,
        bucket_365_1095: 0,
        bucket_1095_plus: 0,
      },
    ]
  }

  async getPayableAgeing(filters: AgeingFilters): Promise<AgeingRow[]> {
    // Mock implementation
    return [
      {
        partyCode: 'VEND-001',
        partyName: 'XYZ Suppliers Ltd',
        category: 'Supplier',
        total: 85000,
        bucket_0_30: 30000,
        bucket_31_90: 25000,
        bucket_90_180: 20000,
        bucket_180_270: 10000,
        bucket_270_365: 0,
        bucket_365_1095: 0,
        bucket_1095_plus: 0,
      },
    ]
  }

  async getEmployees(): Promise<Employee[]> {
    // Mock implementation - from HRM
    return [
      {
        id: 'emp1',
        employeeCode: 'EMP-001',
        name: 'Ahmed Mohamed',
        email: 'ahmed@company.com',
        mobile: '+252-61-9999999',
        department: 'Finance',
        designation: 'Accountant',
        status: 'Active',
        associatedLedger: {
          accountCode: '5100',
          accountName: 'Salaries & Wages - Ahmed',
          balance: 0,
        },
      },
    ]
  }

  async validateUniqueness(
    field: 'partyCode' | 'mobile' | 'address',
    value: string,
    excludeId?: string,
  ): Promise<boolean> {
    // Mock implementation
    console.log('Validating uniqueness:', field, value, excludeId)
    return true // Returns true if unique
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount)
  }
}

export const partyService = new PartyService()
